'use client';
import React, { useEffect, useState, useMemo } from 'react';
import { DirectoryProvider } from '@/components/directory/DirectoryContext';
import { useDynamicAirtableRecords } from '@/lib/airtable/client-hooks';
import { parseAttachments } from '@/lib/airtable/transform';
import Link from 'next/link';
import { FolderOpen } from 'lucide-react';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import MultiSelect from '@/components/ui/multi-select';
import { Pill } from '@/components/ui/Pill';
import { LoadingPattern } from "@/components/ui/pattern-viewer";
import TechnologyDirectorySection from './TechnologyDirectorySection';
import TechnologyFilterBar from './TechnologyFilterBar';
import TechnologyActiveFilters from './TechnologyActiveFilters';
import { Breadcrumb } from '@/components/ui/breadcrumb';

function getDisplayValue(obj: any): string {
  if (!obj || typeof obj !== 'object') return '';
  if (obj.Name) return obj.Name;
  if (obj.name) return obj.name;
  const firstString = Object.values(obj).find(v => typeof v === 'string');
  if (firstString) return String(firstString);
  if (obj.id) return obj.id;
  return '[object]';
}

export default function TechnologiesPage() {
  // Fetch all Technologies
  const { data, isLoading, isError } = useDynamicAirtableRecords('TECHNOLOGY');
  // Fetch schema for dynamic field detection
  const [schema, setSchema] = useState<any>(null);
  const [schemaLoading, setSchemaLoading] = useState(true);

  useEffect(() => {
    setSchemaLoading(true);
    fetch('/api/airtable/schema')
      .then(res => res.json())
      .then(data => setSchema(data))
      .catch(() => setSchema(null))
      .finally(() => setSchemaLoading(false));
  }, []);

  // Find the schema for the current table
  const currentTableSchema = useMemo(
    () => schema?.tables?.find((t: any) => t.name === 'Technology' || t.id === 'TECHNOLOGY'),
    [schema]
  );

  // Helper to extract fields for a Technology record
  function extractFields(technology: any) {
    if (!technology || !currentTableSchema) return {};
    // Use schema's primaryFieldId for the name
    const primaryFieldId = currentTableSchema.primaryFieldId;
    const primaryFieldName = currentTableSchema.fields.find((f: any) => f.id === primaryFieldId)?.name || 'Name';
    // Find description field
    const descriptionField = currentTableSchema.fields.find((f: any) => f.name.toLowerCase().includes('description') || f.name.toLowerCase().includes('summary'))?.name;
    // Slug field
    const slugField = currentTableSchema.fields.find((f: any) => f.name.toLowerCase() === 'slug')?.name;

    // Extract values
    const attachments = (technology['Image'] && Array.isArray(technology['Image']))
      ? parseAttachments(technology['Image']) ?? []
      : [];
    const image = attachments.length > 0 ? attachments[0] : null;
    const imageSrc = image && typeof image === 'object' ? (image.thumbnailLarge ?? image.url ?? '') : '';
    
    const name = technology[primaryFieldName] || '';
    const description = descriptionField ? technology[descriptionField] : '';
    const slug = slugField ? technology[slugField] : undefined;
    return { imageSrc, name, description, slug };
  }

  // Sort Technologies alphabetically by name
  const sortedData = Array.isArray(data) ? [...data].sort((a, b) => {
    const nameA = (extractFields(a).name || '').toLowerCase();
    const nameB = (extractFields(b).name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  }) : [];



  // Add state for filters
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedCROs, setSelectedCROs] = useState<string[]>([]);
  const [selectedPlexTypes, setSelectedPlexTypes] = useState<string[]>([]);
  const [selectedResolution, setSelectedResolution] = useState<string[]>([]);
  const [selectedThroughput, setSelectedThroughput] = useState<string[]>([]);

  // Fetch all Platform records for the filter dropdown
  const { data: platformRecords, isLoading: platformLoading } = useDynamicAirtableRecords('PLATFORMS');
  const platformLookup = useMemo(() => {
    if (!platformRecords || !Array.isArray(platformRecords)) return {};
    const map: Record<string, string> = {};
    platformRecords.forEach((rec: any) => {
      const name = rec['Platform Name'] || rec['Name'] || rec.id;
      map[rec.id] = name;
    });
    return map;
  }, [platformRecords]);

  // Extract unique platforms from Technologies data using the lookup
  const platformOptions = useMemo(() => {
    if (!data || !Array.isArray(data) || !platformLookup) return [];
    const platforms = new Set<string>();
    data.forEach((technology: any) => {
      const platformField = technology['Platforms'];
      if (Array.isArray(platformField)) {
        platformField.forEach((platform) => { 
          if (platform) {
            // Handle both string values and object references
            const platformId = typeof platform === 'string' ? platform : (platform.id || platform);
            const platformName = platformLookup[platformId] || platformId;
            if (platformName) platforms.add(platformName);
          }
        });
      } else if (platformField) {
        // Handle single value
        const platformId = typeof platformField === 'string' ? platformField : (platformField.id || platformField);
        const platformName = platformLookup[platformId] || platformId;
        if (platformName) platforms.add(platformName);
      }
    });
    return Array.from(platforms).sort((a, b) => a.localeCompare(b));
  }, [data, platformLookup]);

  // Fetch all Products records for the filter dropdown
  const { data: productRecords, isLoading: productLoading } = useDynamicAirtableRecords('PRODUCTS');
  const productOptions = useMemo(() => {
    if (!productRecords || !Array.isArray(productRecords)) return [];
    return productRecords
      .map((rec: any) => ({ id: rec.id, name: rec['Name'] || rec['Product Name'] || rec.id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [productRecords]);

  // Fetch all CRO records for the filter dropdown
  const { data: croRecords, isLoading: croLoading } = useDynamicAirtableRecords('CROS');
  const croLookup = useMemo(() => {
    if (!croRecords || !Array.isArray(croRecords)) return {};
    const map: Record<string, string> = {};
    croRecords.forEach((rec: any) => {
      const name = rec['Company'] || rec['CRO'] || rec['Name'] || rec.id;
      map[rec.id] = name;
    });
    return map;
  }, [croRecords]);

  // Extract unique CROs from Technologies data
  const croOptions = useMemo(() => {
    if (!data || !Array.isArray(data) || !croLookup) return [];
    const cros = new Set<string>();
    data.forEach((technology: any) => {
      const croField = technology['CROs'];
      if (Array.isArray(croField)) {
        croField.forEach((cro) => { 
          if (cro) {
            const croName = croLookup[cro] || cro;
            cros.add(croName);
          }
        });
      } else if (croField) {
        const croName = croLookup[croField] || croField;
        cros.add(croName);
      }
    });
    return Array.from(cros).sort((a, b) => a.localeCompare(b));
  }, [data, croLookup]);

  // Extract unique plex type options from Technologies data
  const plexTypeOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const plexTypes = new Set<string>();
    data.forEach((technology: any) => {
      const plexTypeField = technology['Plex Type'];
      if (Array.isArray(plexTypeField)) {
        plexTypeField.forEach((p) => { if (p) plexTypes.add(p); });
      } else if (typeof plexTypeField === 'string' && plexTypeField) {
        plexTypes.add(plexTypeField);
      }
    });
    return Array.from(plexTypes).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique resolution options from Technologies data
  const resolutionOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const resolution = new Set<string>();
    data.forEach((technology: any) => {
      const resolutionField = technology['Resolution'];
      if (Array.isArray(resolutionField)) {
        resolutionField.forEach((r) => { if (r) resolution.add(r); });
      } else if (typeof resolutionField === 'string' && resolutionField) {
        resolution.add(resolutionField);
      }
    });
    return Array.from(resolution).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique throughput options from Technologies data
  const throughputOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const throughput = new Set<string>();
    data.forEach((technology: any) => {
      const throughputField = technology['Throughput'];
      if (Array.isArray(throughputField)) {
        throughputField.forEach((t) => { if (t) throughput.add(t); });
      } else if (typeof throughputField === 'string' && throughputField) {
        throughput.add(throughputField);
      }
    });
    return Array.from(throughput).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Add state for view mode (card or list)
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  
  // Add state for sorting
  const [sortBy, setSortBy] = useState<string>('name-asc');

  // Loading/Error states
  if (isLoading || schemaLoading) {
    return (
      <DirectoryProvider>
        <div className="flex min-h-[60vh] items-center justify-center">
          <LoadingPattern patternName="cell-pattern" size="md" />
        </div>
      </DirectoryProvider>
    );
  }
  if (isError) {
    return (
      <DirectoryProvider>
        <div className="text-center py-20 text-red-500">Error loading Technologies.</div>
      </DirectoryProvider>
    );
  }
  if (!data || !Array.isArray(data) || !currentTableSchema) {
    return (
      <DirectoryProvider>
        <div className="text-center py-20">No Technologies found.</div>
      </DirectoryProvider>
    );
  }

  // Sort and filter technologies
  const filteredAndSortedTechnologies = sortedData.filter((technology: any) => {
    const { name } = extractFields(technology);
    
    // Platform filter logic
    if (selectedPlatforms.length > 0) {
      const platformField = technology['Platforms'];
      if (Array.isArray(platformField)) {
        const technologyPlatformNames = platformField.map((platform) => {
          const platformId = typeof platform === 'string' ? platform : (platform.id || platform);
          return platformLookup[platformId] || platformId;
        });
        if (!technologyPlatformNames.some((platformName) => selectedPlatforms.includes(platformName))) return false;
      } else if (platformField) {
        const platformId = typeof platformField === 'string' ? platformField : (platformField.id || platformField);
        const platformName = platformLookup[platformId] || platformId;
        if (!selectedPlatforms.includes(platformName)) return false;
      } else {
        return false;
      }
    }
    
    // Product filter logic
    if (selectedProducts.length > 0) {
      const productField = technology['Products'];
      if (Array.isArray(productField)) {
        if (!productField.some((product) => selectedProducts.includes(product))) return false;
      } else if (typeof productField === 'string') {
        if (!selectedProducts.includes(productField)) return false;
      } else {
        return false;
      }
    }
    
    // CRO filter logic
    if (selectedCROs.length > 0) {
      const croField = technology['CROs'];
      if (Array.isArray(croField)) {
        const technologyCRONames = croField.map((cro) => {
          return croLookup[cro] || cro;
        });
        if (!technologyCRONames.some((croName) => selectedCROs.includes(croName))) return false;
      } else if (croField) {
        const croName = croLookup[croField] || croField;
        if (!selectedCROs.includes(croName)) return false;
      } else {
        return false;
      }
    }
    
    // Plex Type filter logic
    if (selectedPlexTypes.length > 0) {
      const plexTypeField = technology['Plex Type'];
      if (Array.isArray(plexTypeField)) {
        if (!plexTypeField.some((plex) => selectedPlexTypes.includes(plex))) return false;
      } else if (typeof plexTypeField === 'string') {
        if (!selectedPlexTypes.includes(plexTypeField)) return false;
      } else {
        return false;
      }
    }
    
    // Resolution filter logic
    if (selectedResolution.length > 0) {
      const resolutionField = technology['Resolution'];
      if (Array.isArray(resolutionField)) {
        if (!resolutionField.some((res) => selectedResolution.includes(res))) return false;
      } else if (typeof resolutionField === 'string') {
        if (!selectedResolution.includes(resolutionField)) return false;
      } else {
        return false;
      }
    }
    
    // Throughput filter logic
    if (selectedThroughput.length > 0) {
      const throughputField = technology['Throughput'];
      if (Array.isArray(throughputField)) {
        if (!throughputField.some((thru) => selectedThroughput.includes(thru))) return false;
      } else if (typeof throughputField === 'string') {
        if (!selectedThroughput.includes(throughputField)) return false;
      } else {
        return false;
      }
    }
    
    return !!name && name.trim() !== '';
  }).sort((a: any, b: any) => {
    const { name: nameA } = extractFields(a);
    const { name: nameB } = extractFields(b);
    
    // Get first plex type for sorting
    const getFirstPlexType = (technology: any) => {
      const plexTypeField = technology['Plex Type'];
      if (Array.isArray(plexTypeField) && plexTypeField.length > 0) {
        return plexTypeField[0];
      } else if (plexTypeField) {
        return plexTypeField;
      }
      return '';
    };

    // Get first resolution for sorting
    const getFirstResolution = (technology: any) => {
      const resolutionField = technology['Resolution'];
      if (Array.isArray(resolutionField) && resolutionField.length > 0) {
        return resolutionField[0];
      } else if (resolutionField) {
        return resolutionField;
      }
      return '';
    };
    
    const plexA = getFirstPlexType(a);
    const plexB = getFirstPlexType(b);
    const resA = getFirstResolution(a);
    const resB = getFirstResolution(b);
    
    switch (sortBy) {
      case 'name-asc':
        return nameA.localeCompare(nameB);
      case 'name-desc':
        return nameB.localeCompare(nameA);
      case 'plex-asc':
        return plexA.localeCompare(plexB);
      case 'plex-desc':
        return plexB.localeCompare(plexA);
      case 'resolution-asc':
        return resA.localeCompare(resB);
      case 'resolution-desc':
        return resB.localeCompare(resA);
      default:
        return nameA.localeCompare(nameB);
    }
  });

  return (
    <DirectoryProvider>
      <div className="min-h-screen bg-neutral-50">
        {/* Hero Header */}
        <div className="w-full h-64 md:h-80 bg-gradient-to-r from-primary-500 to-accent-500 shadow-lg overflow-hidden relative rounded-none">
          <div className="max-w-7xl mx-auto w-full h-full flex flex-col md:flex-row md:items-center px-6">
            <div className="w-full">
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                  <span className="text-xl md:text-2xl lg:text-3xl font-medium flex items-center gap-2">
                    <img 
                      src="/LabAtlas-Logo-White-Full.png" 
                      alt="LabAtlas Logo" 
                      className="h-6 w-6 md:h-8 md:w-8 object-contain"
                    />
                    LabAtlas
                  </span>
                Spatial Biology Technologies
              </h1>
              <p className="text-xl md:text-2xl text-white/90 leading-relaxed">
                Explore and compare spatial biology technologies to discover the right technology for your research workflow.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb 
              items={[
                { label: 'Technologies', isCurrentPage: true }
              ]} 
            />
          </div>
          
          {/* Filter Bar */}
          <TechnologyFilterBar
            platformOptions={platformOptions}
            productOptions={productOptions.map(p => p.name)}
            croOptions={croOptions}
            plexTypeOptions={plexTypeOptions}
            resolutionOptions={resolutionOptions}
            throughputOptions={throughputOptions}
            selectedPlatforms={selectedPlatforms}
            setSelectedPlatforms={setSelectedPlatforms}
            selectedProducts={selectedProducts}
            setSelectedProducts={setSelectedProducts}
            selectedCROs={selectedCROs}
            setSelectedCROs={setSelectedCROs}
            selectedPlexTypes={selectedPlexTypes}
            setSelectedPlexTypes={setSelectedPlexTypes}
            selectedResolution={selectedResolution}
            setSelectedResolution={setSelectedResolution}
            selectedThroughput={selectedThroughput}
            setSelectedThroughput={setSelectedThroughput}
            platformLoading={platformLoading}
            productLoading={productLoading}
            croLoading={croLoading}
          />
          {/* Active Filter Pills */}
          <TechnologyActiveFilters
            selectedPlatforms={selectedPlatforms}
            setSelectedPlatforms={setSelectedPlatforms}
            selectedProducts={selectedProducts}
            setSelectedProducts={setSelectedProducts}
            selectedCROs={selectedCROs}
            setSelectedCROs={setSelectedCROs}
            selectedPlexTypes={selectedPlexTypes}
            setSelectedPlexTypes={setSelectedPlexTypes}
            selectedResolution={selectedResolution}
            setSelectedResolution={setSelectedResolution}
            selectedThroughput={selectedThroughput}
            setSelectedThroughput={setSelectedThroughput}
            platformOptions={platformOptions}
            productOptions={productOptions}
            onClearFilters={() => {
              setSelectedPlatforms([]);
              setSelectedProducts([]);
              setSelectedCROs([]);
              setSelectedPlexTypes([]);
              setSelectedResolution([]);
              setSelectedThroughput([]);
            }}
            viewMode={viewMode}
            setViewMode={setViewMode}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
          
          {/* Technologies Display */}
          <TechnologyDirectorySection
            filteredTechnologies={filteredAndSortedTechnologies}
            viewMode={viewMode}
            extractFields={extractFields}
            platformOptions={platformOptions}
            productOptions={productOptions}
            platformLookup={platformLookup}
            selectedPlatforms={selectedPlatforms}
            selectedProducts={selectedProducts}
            selectedCROs={selectedCROs}
            selectedPlexTypes={selectedPlexTypes}
            selectedResolution={selectedResolution}
            selectedThroughput={selectedThroughput}
          />
        </div>
      </div>
      <footer className="w-full py-8 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-4 text-center text-neutral-500 text-sm">
          © 2025 LabAtlas. All rights reserved.
    </div>
      </footer>
    </DirectoryProvider>
  );
} 